import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';

interface Document {
  id: string;
  title: string;
}

const Documents: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastKey, setLastKey] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const { apiCall } = useAuthenticatedApi();
  const fetchedRef = useRef(false);

  const fetchDocuments = useCallback(
    async (last?: string) => {
      if (fetchedRef.current && !last) return; // Prevent duplicate initial fetch
      setIsLoading(true);
      try {
        const payload = last ? { last } : {};
        const response = await apiCall<{ data: Document[] }>('/list-docs', 'POST', payload);
        if (response === undefined) return;
        if (response.data.length === 0) {
          setHasMore(false);
        } else {
          setDocuments((prev) => {
            const newDocs = response.data.filter((doc) => !prev.some((prevDoc) => prevDoc.id === doc.id));
            return [...prev, ...newDocs];
          });
          setLastKey(response.data[response.data.length - 1].id);
        }
        fetchedRef.current = true;
      } catch (err) {
        setError(err instanceof Error ? err : new Error('An unknown error occurred'));
      } finally {
        setIsLoading(false);
      }
    },
    [apiCall]
  );

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleLoadMore = useCallback(() => {
    if (lastKey) {
      fetchDocuments(lastKey);
    }
  }, [lastKey, fetchDocuments]);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await apiCall(`/doc?id=${id}`, 'DELETE');
        setDocuments((prevDocs) => prevDocs.filter((doc) => doc.id !== id));
      } catch (err) {
        console.error('Error deleting document:', err);
        setError(err instanceof Error ? err : new Error('An error occurred while deleting the document'));
      } finally {
        setDeleteConfirm(null);
      }
    },
    [apiCall]
  );

  if (error) {
    throw error;
  }

  return (
    <div className="w-full px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">All Documents</h1>
        <Button asChild>
          <Link
            to="/edit-doc"
            className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Document
          </Link>
        </Button>
      </div>
      {isLoading && documents.length === 0 ? (
        <p>Loading documents...</p>
      ) : (
        <>
          <ul className="space-y-4">
            {documents.map((doc) => (
              <li key={doc.id} className="flex items-center justify-between bg-white p-4 rounded-lg shadow">
                <Link to={`/edit-doc?id=${doc.id}`} className="text-blue-600 hover:underline">
                  {doc.title}
                </Link>
                <button
                  onClick={() => setDeleteConfirm(doc.id)}
                  className="text-red-500 hover:text-red-700"
                  aria-label={`Delete ${doc.title}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
          {hasMore && (
            <Button onClick={handleLoadMore} className="mt-4" disabled={isLoading}>
              {isLoading ? 'Loading...' : 'Load More'}
            </Button>
          )}
        </>
      )}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg max-w-sm w-full">
            <h2 className="text-xl font-bold mb-4">Confirm Deletion</h2>
            <p className="mb-4">Are you sure you want to delete this document? This action cannot be undone.</p>
            <div className="flex justify-end space-x-4">
              <Button onClick={() => setDeleteConfirm(null)} variant="outline">
                Cancel
              </Button>
              <Button onClick={() => deleteConfirm && handleDelete(deleteConfirm)} variant="destructive">
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;
